import { useEffect, useState } from "react"
import {
  getSprintBlocksByClientId,
  getSprintCyclesByClientId,
} from "../../../services/clientReport.service"
import useToast from "../../../hooks/useToast.hook"
import { toastMessages } from "../../../consts/common.const"

const getNameNumericSegments = (name = "") => {
  const nameAsString = String(name)
  const match = nameAsString.match(/(\d+(?:\.\d+)*)/)

  if (!match?.[1]) {
    return []
  }

  return match[1]
    .split(".")
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part))
}

const compareNamesByNumericSegmentsDesc = (nameA = "", nameB = "") => {
  const numericPartsA = getNameNumericSegments(nameA)
  const numericPartsB = getNameNumericSegments(nameB)
  const maxLength = Math.max(numericPartsA.length, numericPartsB.length)

  for (let i = 0; i < maxLength; i++) {
    const partA = numericPartsA[i]
    const partB = numericPartsB[i]

    if (partA == null && partB != null) return 1
    if (partA != null && partB == null) return -1
    if (partA !== partB) return partB - partA
  }

  return String(nameB).localeCompare(String(nameA), "es")
}

const getBlockName = (block = {}) => {
  return block?.name || block?.sprintBlockName || block?.blockName || ""
}

const getCycleName = (cycle = {}) => {
  return cycle?.name || cycle?.sprintCycleName || cycle?.cycleName || ""
}

const useClientReportPeriods = ({ clientId, refresh }) => {
  const { setErrorMessage } = useToast()

  const [sprintBlocks, setSprintBlocks] = useState([])
  const [sprintCycles, setSprintCycles] = useState([])

  useEffect(() => {
    if (clientId) {
      getData()
      return
    }

    setSprintBlocks([])
    setSprintCycles([])
  }, [clientId, refresh])

  const getData = async () => {
    const [resBlocks, resCycles] = await Promise.all([
      getSprintBlocksByClientId(clientId),
      getSprintCyclesByClientId(clientId),
    ])

    if (!resBlocks.success || !resCycles.success) {
      setErrorMessage({
        title: toastMessages.dataDoesNotLoad.headline,
        message: toastMessages.dataDoesNotLoad.details,
      })
      setSprintBlocks([])
      setSprintCycles([])
      return
    }

    const sortedBlocks = [...resBlocks.result].sort((a, b) =>
      compareNamesByNumericSegmentsDesc(getBlockName(a), getBlockName(b))
    )
    const sortedCycles = [...resCycles.result].sort((a, b) =>
      compareNamesByNumericSegmentsDesc(getCycleName(a), getCycleName(b))
    )

    setSprintBlocks(sortedBlocks)
    setSprintCycles(sortedCycles)
  }

  return { sprintBlocks, sprintCycles }
}

export default useClientReportPeriods
